import React, { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import axios from "axios";
import styled from "styled-components";
import Spinner from "../../../components/dashboard/Spinner";
import DashboardLayout from "../../../components/dashboard/DashboardLayout";
import HeaderSection from "../../../components/dashboard/HeaderSection";
import FiltersSection from "../../../components/dashboard/FiltersSection";
import StatsGrid from "../../../components/dashboard/StatsGrid";
import ChartsSection from "../../../components/dashboard/ChartsSection";
import LeadsTableSection from "../../../components/dashboard/LeadsTableSection";

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  gap: 1rem;
  position: relative;
  z-index: 3;

  p {
    color: #666;
    font-size: 1.2rem;
  }
`;

const DashboardContainer = styled.div`
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
`;

const DashboardContent = styled.div`
  max-width: 1800px;
  margin: 0 auto;
`;

const Dashboard = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [userPlan, setUserPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("mensal");
  const [reallocateLeads, setReallocateLeads] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 10,
  });
  const [tableLoading, setTableLoading] = useState(false);
  const [dashboardStats, setDashboardStats] = useState(null);
  const dashboardRef = useRef();

  const buildApiUrl = (endpoint) => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:8087";
    return `${baseUrl.replace(/\/+$/, "")}/${endpoint.replace(/^\/+/, "")}`;
  };

  // Função para buscar estatísticas do dashboard
  const fetchDashboardStats = useCallback(async (period) => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const token = user.token;

      if (!token) {
        throw new Error("Usuário não autenticado");
      }

      const apiPeriod = {
        diario: 'daily',
        semanal: 'weekly',
        mensal: 'monthly',
        anual: 'yearly'
      }[period] || 'monthly';

      const response = await axios.get(buildApiUrl("dashboard/stats"), {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        params: { period: apiPeriod }
      });

      setDashboardStats(response.data);
    } catch (error) {
      console.error("Erro ao carregar estatísticas:", error);
      enqueueSnackbar(
        error.response?.data?.message || "Erro ao carregar estatísticas do dashboard", 
        { variant: "error" }
      );
    }
  }, [enqueueSnackbar]);

  // Função para buscar leads de realocação
  const fetchLeads = useCallback(
    async (page = 1, size = 10, isTableUpdate = false) => {
      try {
        if (isTableUpdate) {
          setTableLoading(true);
        } else {
          setIsLoading(true);
        }
        
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const token = user.token;

        if (!token) {
          throw new Error("Usuário não autenticado");
        }

        const response = await axios.get(buildApiUrl("relocation-leads"), {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
          params: { page, size },
        });

        setReallocateLeads(response.data.data || []);
        setPagination({
          currentPage: response.data.meta?.currentPage + 1 || 1,
          totalPages: response.data.meta?.totalPages || 1,
          totalItems: response.data.meta?.totalItems || 0,
          pageSize: response.data.meta?.pageSize || size,
        });
        setLastUpdated(new Date());
      } catch (error) {
        console.error("Erro ao carregar leads:", error);
        enqueueSnackbar("Erro ao carregar leads de realocação", { variant: "error" });
      } finally {
        if (isTableUpdate) {
          setTableLoading(false);
        } else {
          setIsLoading(false);
        }
        setIsRefreshing(false);
      }
    },
    [enqueueSnackbar]
  );

  // Efeito para carregar dados iniciais
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);
        const userString = localStorage.getItem("user");
        const user = userString ? JSON.parse(userString) : null;
        const plan = user?.plan || "Free";
        setUserPlan(plan);

        if (plan !== "Free") {
          await Promise.all([
            fetchDashboardStats(activeFilter),
            fetchLeads()
          ]);
        }
      } catch (error) {
        console.error("Erro ao carregar dados iniciais:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [fetchDashboardStats, fetchLeads, activeFilter]);

  // Efeito para atualizar estatísticas quando o filtro muda
  useEffect(() => {
    if (userPlan !== 'Free') {
      fetchDashboardStats(activeFilter);
    }
  }, [activeFilter, userPlan, fetchDashboardStats]);

  const handleNewRequest = (e) => {
    e.preventDefault();
    navigate("/realocation/register");
  };

  const handleRefreshLeads = async (e) => {
    if (e) e.preventDefault();
    setIsRefreshing(true);
    await fetchLeads(pagination.currentPage, pagination.pageSize, true);
    enqueueSnackbar('Leads atualizados com sucesso!', { variant: 'success' });
  };

  const handlePageChange = async (e, newPage) => {
    e.preventDefault();
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      await fetchLeads(newPage, pagination.pageSize, true);
    }
  };

  const handleRefreshStats = async () => {
    await fetchDashboardStats(activeFilter);
    enqueueSnackbar('Estatísticas atualizadas!', { variant: 'success' });
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner />
        <p>Carregando dashboard...</p>
      </LoadingContainer>
    );
  }

  return (
    <DashboardContainer>
      <DashboardContent ref={dashboardRef}>
        <DashboardLayout>
          <HeaderSection 
            userPlan={userPlan} 
            onNewRequest={handleNewRequest} 
            reallocateLeads={reallocateLeads}
            dashboardRef={dashboardRef}
          />

          <FiltersSection 
            activeFilter={activeFilter} 
            setActiveFilter={setActiveFilter} 
            userPlan={userPlan} 
          />

          <StatsGrid
            userPlan={userPlan}
            activeFilter={activeFilter}
            stats={dashboardStats}
            onRefresh={handleRefreshStats}
          />

          <ChartsSection 
            activeFilter={activeFilter} 
            setActiveFilter={setActiveFilter} 
            stats={dashboardStats}
          />

          {userPlan !== 'Free' && (
            <LeadsTableSection
              reallocateLeads={reallocateLeads}
              tableLoading={tableLoading}
              lastUpdated={lastUpdated}
              isRefreshing={isRefreshing}
              handleRefreshLeads={handleRefreshLeads}
              pagination={pagination}
              handlePageChange={handlePageChange}
            />
          )}
        </DashboardLayout>
      </DashboardContent>
    </DashboardContainer>
  );
};

export default Dashboard;